import React, { useState, useEffect } from 'react';
import {useNavigate, useParams} from "react-router-dom";
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import useApi from "../../../utils/useApi";
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Swal from "sweetalert2";

const SendLetter = ({handleShowHeader, handleShowSettingsHeader}) => {
    const { getEmailTemplate, submitLetter } = useApi();
    const { uid, template_id } = useParams();
    const [template, setTemplate] = useState(null);
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(false);
    const [contentReady, setContentReady] = useState(false);
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
                                                 email: '',
                                                 emailSubject: '',
                                             });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        initialize();
    }, []);

    const initialize = async () => {
        try {
            setLoading(true);
            const response = await getEmailTemplate(template_id, uid);
            setTemplate(response.template);
            setClient(response.client);
        } catch (error) {
            console.error('Error fetching templates:', error);
            setTemplate(null);
            setClient(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!client || !template) return;

        try {
            const currentDate = new Date().toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });

            let processedBody = template.body
                                        .replace('{$Refund}', client.profile?.refund_amount ? '$' + client.profile?.refund_amount : '(no refund specified)')
                                        .replace('$FEE', client.profile?.tax_prep_fee ? '$' + client.profile?.tax_prep_fee : '(no fee specified)');

            const initialContent = `
                <p>${currentDate}</p>
                <br/>
                <p>Dear ${client.profile?.client || ''},</p>
                ${processedBody}
            `;

            // Convert HTML to Draft.js content
            const contentBlock = htmlToDraft(initialContent);
            if (contentBlock) {
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                const editorState = EditorState.createWithContent(contentState);
                setEditorState(editorState);
            }

            setFormData(prev => ({
                ...prev,
                email: client.email || prev.email,
                emailSubject: template.subject || prev.emailSubject
            }));

            setContentReady(true);
        } catch (error) {
            console.error('Error processing template content:', error);
        }
    }, [client, template]);

    const handleEditorStateChange = (newEditorState) => {
        setEditorState(newEditorState);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Convert Draft.js content to HTML
            const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));

            const formPayload = {
                client_id: client.id,
                name: client.profile.account_name,
                template_id: template.id,
                template_content: htmlContent,
                subject: formData.email,
                email_subject: formData.emailSubject,
                temp_email_data: `
                    <div class="modal-body" style="background-color:white;">
                        <div id="myModalHeader">
                            ${template.header}
                        </div>
                        <div id="myModalBody">${htmlContent}</div>
                        <div id="myModalfooter">
                            ${template.footer}
                        </div>
                    </div>`
            };

            const response = await submitLetter(formPayload);
            console.log(response);

            if(response.status === 'success') {
                showSuccessAlert(response.message);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const showSuccessAlert = (message) => {
        Swal.fire({
                      title: 'Success!',
                      text: message,
                      icon: 'success',
                      confirmButtonText: 'Continue',
                      confirmButtonColor: '#3085d6'
                  }).then((result) => {
            if (result.isConfirmed) {
                navigate('/clients/' + client.id);
            }
        });
    };

    const renderEditor = () => {
        if (!contentReady) {
            return (
                <div className="min-h-[300px] p-4 bg-white dark:bg-gray-700">
                    Loading...
                </div>
            );
        }

        return (
            <div className="bg-white dark:bg-gray-700 min-h-[400px] border border-gray-300 dark:border-gray-600 rounded-lg">
                <Editor
                    editorState={editorState}
                    onEditorStateChange={handleEditorStateChange}
                    toolbar={{
                        options: ['inline', 'blockType', 'list', 'textAlign', 'history'],
                        inline: {
                            options: ['bold', 'italic', 'underline'],
                        },
                        blockType: {
                            options: ['Normal', 'H1', 'H2', 'H3'],
                        },
                        list: {
                            options: ['unordered', 'ordered'],
                        },
                    }}
                    editorClassName="px-4 py-2 min-h-[350px] text-gray-900 dark:text-gray-100"
                />
            </div>
        );
    };

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6 page-container relative z-10">
            <div className="container mx-auto">
                <div className="w-full">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
                        <div className="border-b border-gray-200 dark:border-gray-700 p-4 md:p-6">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                                {template?.title}
                            </h2>
                        </div>

                        <div className="p-4 md:p-6">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="prose dark:prose-invert max-w-none">
                                    <div
                                        className="text-center py-8 px-4 md:px-8 text-gray-800 dark:text-gray-200"
                                        dangerouslySetInnerHTML={{ __html: template?.header || '' }}
                                    />
                                </div>

                                <div className="bg-white dark:bg-gray-700 rounded-lg">
                                    {renderEditor()}
                                </div>

                                <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
                                    <div className="max-w-md">
                                        <input
                                            required
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                            placeholder="Enter Email"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                                     focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                                                     focus:border-transparent"
                                        />
                                    </div>

                                    <div className="max-w-md">
                                        <input
                                            required
                                            type="text"
                                            value={formData.emailSubject}
                                            onChange={(e) => setFormData(prev => ({ ...prev, emailSubject: e.target.value }))}
                                            placeholder="Enter Subject"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                                     focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                                                     focus:border-transparent"
                                        />
                                    </div>

                                    <div className="flex justify-center">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg
                                                     transition-colors duration-200 disabled:opacity-50
                                                     disabled:cursor-not-allowed focus:outline-none focus:ring-2
                                                     focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                                        >
                                            {isSubmitting ? 'Sending...' : 'Send'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendLetter;
